import { useDisclosure } from '@mantine/hooks';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { IncidentDrawerContext } from './IncidentDrawerContextStore';

export function IncidentDrawerProvider({ children }: { children: ReactNode }) {
	const [incidentId, setIncidentId] = useState<string | null>(null);
	const [opened, { open, close }] = useDisclosure(false);

	// Queue up the incident to be opened after the monitor page is loaded.
	const [queueStatus, setQueueStatus] = useState<
		'unqueued' | 'queued' | 'opened'
	>('unqueued');
	const location = useLocation();

	const handleOpen = useCallback(
		(incident: string | null) => {
			setQueueStatus('opened');
			setIncidentId(incident);
			if (incident) {
				open();
			}
		},
		[setIncidentId, open]
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const incident = params.get('incident');
		if (incident && queueStatus === 'unqueued') {
			setQueueStatus('queued');
			setTimeout(() => {
				// Use a functional update to ensure the latest value is checked
				setQueueStatus((prevQueued) => {
					if (prevQueued === 'queued') {
						handleOpen(incident);
					}
					return prevQueued; // Return the previous value to avoid overriding it
				});
			}, 600);
		}
	}, [location.search, handleOpen, queueStatus]);

	const contextValue = useMemo(
		() => ({
			incidentId: incidentId,
			openIncident: handleOpen,
			opened,
			close,
		}),
		[incidentId, handleOpen, opened, close]
	);

	return (
		<IncidentDrawerContext.Provider value={contextValue}>
			{children}
		</IncidentDrawerContext.Provider>
	);
}
